import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { Video, Plus, RefreshCw } from "lucide-react";
import { AppButton, EmptyState, ConfirmModal, AppModal } from "../ui";
import VideoCard from "./VideoCard";
import VideoPlayerModal from "./VideoPlayerModal";
import UploadVideoModal from "./UploadVideoModal";
import MapMaterialModal from "../Materials/MapMaterialModal";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import "./Videos.scss";

const BatchVideosPage = ({ batchId, role, currentUserId }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page] = useState(1);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Selected video for player
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);

  // Edit modal
  const [editVideo, setEditVideo] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", topic: "", recordedAt: "" });
  const [saving, setSaving] = useState(false);

  const isAdmin = role === "admin" || role === "superadmin";
  const isInstructor = role === "instructor";
  const canUpload = isAdmin || isInstructor;

  // Material linking
  const [matModal, setMatModal] = useState({ show: false, video: null });
  const [linkedMatCounts, setLinkedMatCounts] = useState({}); // { videoId: count }
  const [linkedMatIds, setLinkedMatIds] = useState({}); // { videoId: [materialId, ...] }

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/videos/batch/${batchId}`, {
        params: { page, limit: 50 },
      });
      if (data.success) {
        setVideos(data.data || []);
        setTotal(data.pagination?.total || 0);
      }
    } catch {
      setToast({ show: true, message: "Failed to load videos.", type: "error" });
    }
    setLoading(false);
  }, [batchId, page]);

  useEffect(() => {
    if (batchId) fetchVideos();
  }, [batchId, fetchVideos]);

  // ── Fetch linked material counts per video ──
  const fetchLinkedMatCounts = useCallback(async () => {
    if (!batchId || !videos.length) return;
    try {
      const counts = {};
      const ids = {};
      await Promise.all(
        videos.map(async (v) => {
          try {
            const { data } = await apiClient.get(`/materials/batch/${batchId}/by-video/${v._id}`);
            const mats = data.data || [];
            counts[v._id] = mats.length;
            ids[v._id] = mats.map((m) => m._id);
          } catch {
            counts[v._id] = 0;
            ids[v._id] = [];
          }
        })
      );
      setLinkedMatCounts(counts);
      setLinkedMatIds(ids);
    } catch {}
  }, [batchId, videos]);

  useEffect(() => {
    fetchLinkedMatCounts();
  }, [fetchLinkedMatCounts]);

  // ── Play ──
  const handlePlay = (video) => {
    setSelectedVideo(video);
    setShowPlayer(true);
  };

  const handleClosePlayer = () => {
    setShowPlayer(false);
    setSelectedVideo(null);
  };

  // ── Edit ──
  const handleEditOpen = (video) => {
    setEditVideo(video);
    setEditForm({
      title: video.title || "",
      description: video.description || "",
      topic: video.topic || "",
      recordedAt: video.recordedAt ? new Date(video.recordedAt).toISOString().split("T")[0] : "",
    });
    setShowEdit(true);
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        topic: editForm.topic.trim(),
      };
      if (editForm.recordedAt) payload.recordedAt = editForm.recordedAt;
      const { data } = await apiClient.put(`/videos/${editVideo._id}`, payload);
      if (data.success) {
        setToast({ show: true, message: "Video updated.", type: "success" });
        setShowEdit(false);
        setEditVideo(null);
        fetchVideos();
      }
    } catch {
      setToast({ show: true, message: "Failed to update video.", type: "error" });
    }
    setSaving(false);
  };

  // ── Delete ──
  const [deleteVideo, setDeleteVideo] = useState(null);

  const handleDelete = (video) => setDeleteVideo(video);

  const confirmDelete = async () => {
    const video = deleteVideo;
    setDeleteVideo(null);
    try {
      const { data } = await apiClient.delete(`/videos/${video._id}`);
      if (data.success) {
        setToast({ show: true, message: "Video deleted.", type: "success" });
        fetchVideos();
      }
    } catch {
      setToast({ show: true, message: "Failed to delete video.", type: "error" });
    }
  };

  // ── Link Material ──
  const handleOpenLinkMaterial = (video) => {
    setMatModal({ show: true, video });
  };

  const handleLinkMaterial = async (materialId) => {
    await apiClient.put(`/materials/${materialId}/link-video`, { videoId: matModal.video._id });
    setMatModal({ show: false, video: null });
    setToast({ show: true, message: "Material linked to video.", type: "success" });
    fetchLinkedMatCounts();
  };

  // ── Upload success ──
  const handleUploadSuccess = () => {
    setToast({ show: true, message: "Video uploaded successfully!", type: "success" });
    fetchVideos();
  };

  // ── Sync recordings from Zoom (admin/instructor only) ──
  // Used when a `recording.completed` webhook never arrived or when an
  // earlier upload failed mid-stream (S3 object exists but is only KB-sized).
  // Calls the BE which fetches directly from Zoom's REST API and re-runs the
  // ingest pipeline. Idempotent — already-ready videos are skipped.
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  // Mode selection on the batch-level sync modal. The earlier 'meeting'
  // mode was removed because admins don't know raw Zoom meeting IDs —
  // per-meeting re-sync is now exposed on each VideoCard's action menu
  // (the BE already knows the meetingId from the BatchVideo row).
  const [syncMode, setSyncMode] = useState("recent"); // 'recent' | 'date'
  const [syncDate, setSyncDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const handleSyncFromZoom = async () => {
    setSyncing(true);
    try {
      const body = syncMode === "date" && syncDate ? { date: syncDate } : {};
      const { data } = await apiClient.post(`/batches/${batchId}/sync-recordings`, body);
      setToast({
        show: true,
        message: data?.message || "Sync complete.",
        type: "success",
      });
      setShowSyncModal(false);
      fetchVideos();
    } catch (err) {
      const msg = err?.response?.data?.message || "Sync failed. Please try again.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setSyncing(false);
  };

  // Per-video re-sync. Called from the VideoCard action menu — the card
  // already knows the meetingId, so the user never has to type it.
  const [syncingVideoId, setSyncingVideoId] = useState(null);
  const handleSyncVideoFromZoom = async (video) => {
    if (!video?.meetingId) {
      setToast({ show: true, message: "This video has no Zoom meeting ID — cannot re-sync.", type: "error" });
      return;
    }
    setSyncingVideoId(video._id);
    try {
      const { data } = await apiClient.post(
        `/batches/${batchId}/sync-recordings`,
        { meetingId: String(video.meetingId) }
      );
      setToast({
        show: true,
        message: data?.message || "Sync complete.",
        type: "success",
      });
      fetchVideos();
    } catch (err) {
      const msg = err?.response?.data?.message || "Sync failed. Please try again.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setSyncingVideoId(null);
  };

  return (
    <div className="videos-page">
      {/* Header */}
      <div className="videos-page__header">
        <div className="d-flex align-items-center gap-2">
          <h3 className="videos-page__title">
            <Video size={18} />
            Videos
          </h3>
          {total > 0 && <span className="videos-page__count">{total}</span>}
        </div>

        {canUpload && (
          <div className="d-flex align-items-center gap-2">
            <AppButton
              variant="outline"
              size="sm"
              onClick={() => setShowSyncModal(true)}
              disabled={syncing}
              title="Re-fetch missed or failed recordings from Zoom for this batch."
            >
              <RefreshCw size={14} className={syncing ? "spin" : undefined} />
              {syncing ? "Syncing…" : "Sync from Zoom"}
            </AppButton>
            <AppButton variant="default" size="sm" onClick={() => setShowUpload(true)}>
              <Plus size={14} />
              Upload Video
            </AppButton>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="videos-spinner">
          <Spinner animation="border" />
        </div>
      ) : videos.length === 0 ? (
        <EmptyState
          icon={<Video size={40} />}
          title="No videos yet"
          description={canUpload ? "Upload a class recording to get started." : "Class recordings will appear here."}
          action={
            canUpload ? (
              <AppButton variant="default" size="sm" onClick={() => setShowUpload(true)}>
                <Plus size={14} />
                Upload Video
              </AppButton>
            ) : null
          }
        />
      ) : (
        <div className="videos-page__grid">
          {videos.map((video) => (
            <VideoCard
              key={video._id}
              video={video}
              role={role}
              currentUserId={currentUserId}
              onPlay={handlePlay}
              onEdit={handleEditOpen}
              onDelete={handleDelete}
              onLinkMaterial={handleOpenLinkMaterial}
              onSyncFromZoom={canUpload ? handleSyncVideoFromZoom : undefined}
              syncing={syncingVideoId === video._id}
              linkedMaterialCount={linkedMatCounts[video._id] || 0}
            />
          ))}
        </div>
      )}

      {/* Video Player Modal */}
      <VideoPlayerModal
        video={selectedVideo}
        show={showPlayer}
        onClose={handleClosePlayer}
        role={role}
        batchId={batchId}
      />

      {/* Upload Modal */}
      <UploadVideoModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        batchId={batchId}
        onSuccess={handleUploadSuccess}
      />

      {/* Edit Modal */}
      <AppModal
        show={showEdit}
        onHide={() => !saving && setShowEdit(false)}
        title="Edit Video"
        size="sm"
        closeOnOverlay={!saving}
        footer={
          <>
            <AppButton variant="outline" size="sm" onClick={() => setShowEdit(false)} disabled={saving}>
              Cancel
            </AppButton>
            <AppButton variant="default" size="sm" onClick={handleEditSave} disabled={saving || !editForm.title.trim()}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </AppButton>
          </>
        }
      >
        <div className="edit-video-modal__fields">
          <div className="edit-video-modal__group">
            <label className="edit-video-modal__label">Title</label>
            <input
              type="text"
              className="edit-video-modal__input"
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div className="edit-video-modal__group">
            <label className="edit-video-modal__label">Topic</label>
            <input
              type="text"
              className="edit-video-modal__input"
              value={editForm.topic}
              onChange={(e) => setEditForm((f) => ({ ...f, topic: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div className="edit-video-modal__group">
            <label className="edit-video-modal__label">Recorded Date</label>
            <input
              type="date"
              className="edit-video-modal__input"
              value={editForm.recordedAt}
              onChange={(e) => setEditForm((f) => ({ ...f, recordedAt: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div className="edit-video-modal__group">
            <label className="edit-video-modal__label">Description</label>
            <textarea
              className="edit-video-modal__input edit-video-modal__textarea"
              rows={3}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              disabled={saving}
            />
          </div>
        </div>
      </AppModal>

      {/* Link Material Modal */}
      <MapMaterialModal
        show={matModal.show}
        batchId={batchId}
        targetLabel={matModal.video?.title || ""}
        alreadyLinkedIds={matModal.video ? (linkedMatIds[matModal.video._id] || []) : []}
        onLink={handleLinkMaterial}
        onClose={() => setMatModal({ show: false, video: null })}
      />

      {/* Toaster */}
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteVideo}
        onHide={() => setDeleteVideo(null)}
        onConfirm={confirmDelete}
        title="Delete Video"
        message={`Delete "${deleteVideo?.title}"? This will also remove all its comments.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Sync from Zoom Modal */}
      <AppModal
        show={showSyncModal}
        onHide={() => !syncing && setShowSyncModal(false)}
        title="Sync Recordings from Zoom"
        icon={<RefreshCw size={16} />}
        size="sm"
        closeOnOverlay={!syncing}
        footer={
          <>
            <AppButton variant="ghost" size="sm" onClick={() => setShowSyncModal(false)} disabled={syncing}>
              Cancel
            </AppButton>
            <AppButton
              variant="default"
              size="sm"
              onClick={handleSyncFromZoom}
              disabled={syncing || (syncMode === "date" && !syncDate)}
            >
              <RefreshCw size={14} className={syncing ? "spin" : undefined} />
              {syncing ? "Syncing…" : "Sync Now"}
            </AppButton>
          </>
        }
      >
        <p className="videos-page__sync-help">
          Re-fetches missed or failed Zoom recordings for this batch and ingests them.
          Already-synced recordings are skipped.
        </p>

        <div className="sync-modal__options">
          <label className="sync-modal__option">
            <input
              type="radio"
              name="syncMode"
              value="recent"
              checked={syncMode === "recent"}
              onChange={() => setSyncMode("recent")}
              disabled={syncing}
            />
            <span>Last 24 hours</span>
          </label>

          <label className="sync-modal__option">
            <input
              type="radio"
              name="syncMode"
              value="date"
              checked={syncMode === "date"}
              onChange={() => setSyncMode("date")}
              disabled={syncing}
            />
            <span>Specific date</span>
          </label>

          {syncMode === "date" && (
            <div className="sync-modal__date-group">
              <input
                type="date"
                className="sync-modal__date-input"
                value={syncDate}
                onChange={(e) => setSyncDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                disabled={syncing}
              />
              <p className="sync-modal__hint">Pulls all recordings for this batch on the selected date (IST).</p>
            </div>
          )}
        </div>

        <p className="sync-modal__tip">
          Tip: to re-sync a single recording, use the “Sync from Zoom” option in that video card’s action menu.
        </p>
      </AppModal>
    </div>
  );
};

export default BatchVideosPage;
